/**
 * The planting vocabulary — the shapes every part of this feature describes a mass of plants with.
 *
 * The important design decision in this feature is that the model does *not* author the project
 * document. It describes planting **masses** — "38 red maples scattered through these three bands",
 * not 38 individual trees — and a deterministic compiler expands that into the actual planting
 * (`PlantingPlacement`, `SitePipelineCompiler`).
 *
 * That split is not cosmetic. There are four things a language model structurally cannot do here:
 *
 * 1. **Ground height.** `position.y` must equal the terrain height at (x, z), and nothing re-snaps
 *    it on load. The model has never seen the heightfield; only the app can sample it.
 * 2. **Collision-free distribution.** Placing 190 plants that respect per-species spacing and dodge
 *    every building footprint is a rejection-sampling problem, not a language one.
 * 3. **Determinism.** Regenerating a site must be reproducible; sampling from a model is not.
 * 4. **Volume.** A full document is ~100 kB of JSON. Asking for it wastes tokens, invites
 *    truncation, and turns one bad number into a corrupt file.
 *
 * What a model *is* uniquely good at is the part the code cannot do: reading a photograph — the
 * scale anchor, the season from the foliage, the sun from the shadows, which mass is a hedge and
 * which is woodland, and what the buildings are.
 *
 * These types are the seam between the two halves, and three separate readers parse into them: the
 * pipeline's flora stage (`SitePipeline`), the magic wand (`wand/WandOps`), and the `sites/`
 * authoring scripts. Each keeps its own parser, because each is forgiving about different things —
 * what they share is the shape, which is what the placement engine consumes.
 *
 * A mass is also a perfectly good hand-authored artifact, which is why every test in this feature
 * runs without an API key.
 */

/** Thrown when model output cannot be read. Carries a message meant for the user. */
export class SitePlanFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SitePlanFormatError';
  }
}

export interface PlanRect {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

export interface PlanDrift {
  x: number;
  z: number;
  radiusMeters: number;
  count: number;
}

export interface PlanLine {
  /** Polyline vertices in world meters, `[x, z]`. */
  points: [number, number][];
  spacingMeters: number;
  /** Sideways offset, so a second rank runs parallel to the first. */
  offsetMeters?: number;
  jitterMeters?: number;
}

/**
 * One planting mass. Exactly one placement form should be present; the compiler prefers `line`,
 * then `drifts`, then `regions`, and reports when a mass supplies none.
 */
export interface PlanPlanting {
  id: string;
  name: string;
  /** Must be an id in this build's flora catalog. */
  speciesId: string;
  /**
   * Spacing is enforced *within* a layer and ignored across layers, because shrubs belong under
   * trees. Any string works; masses sharing a name compete for room.
   */
  layer: string;
  minSpacingMeters: number;
  /** `[min, max]`; every member draws its own value, because a real mass is every age at once. */
  maturity: [number, number];
  /** Display name of the `PlantGroup` this mass becomes. */
  group?: string;
  /** True when the mass is genuinely one repeated plant (a clipped hedge) — becomes an alias group. */
  linked?: boolean;
  pruningStyle?: string;
  /** Scatter `count` plants across these bands. */
  regions?: PlanRect[];
  count?: number;
  /** Clustered drifts — the antidote to an even wash. */
  drifts?: PlanDrift[];
  /** March along a polyline: a hedge, a screen, an avenue. */
  line?: PlanLine;
  /**
   * An arbitrary boundary the mass must stay inside, `[x, z]` world metres, closed implicitly.
   *
   * A boundary rather than a fifth placement form. Rectangles cannot express a bed someone drew by
   * hand, and the shape they drew is the whole point of having drawn it — so this constrains
   * whichever form is present, and fills itself with `count` when no other form is given.
   */
  polygon?: [number, number][];
}
